import {DateTime} from 'luxon';
import {DateFilter} from '../dates';
import {
    FilterMetadataMap,
    FilterType,
    FilterValueByType,
    NumberFilter,
    NumberOperator,
} from './types';

export type NullableFilterValuesByDefinition<
    TDefinition extends Record<string, FilterType>,
> = {
    [K in keyof TDefinition]: FilterValueByType<TDefinition[K]>;
};

type DtoFieldShape<
    TDefinition extends Record<string, FilterType>,
    K extends keyof TDefinition,
    TMetadata extends FilterMetadataMap<TDefinition> | undefined = undefined,
> = TDefinition[K] extends 'DateFilter'
    ? K extends string
        ? {
            [P in `${K}_start` | `${K}_end`]?: string;
        }
        : never
    : TDefinition[K] extends 'number'
        ? K extends string
            ? {
                [P in `${K}_op`]?: NumberOperator;
            } & {
                [P in `${K}_value` | `${K}_min` | `${K}_max`]?: number;
            }
            : never
        : TDefinition[K] extends 'boolean'
            ? {
                [P in K]?: boolean;
            }
    : TDefinition[K] extends 'string'
        ? {
            [P in K]?: TMetadata extends FilterMetadataMap<TDefinition>
                ? TMetadata[K] extends {
                    kind: 'enum';
                    enumValues: readonly string[];
                }
                    ? TMetadata[K]['enumValues'][number]
                    : string
                : string;
        }
        : TDefinition[K] extends 'string[]'
            ? {
                [P in K]?: TMetadata extends FilterMetadataMap<TDefinition>
                    ? TMetadata[K] extends {
                        kind: 'enum';
                        enumValues: readonly string[];
                    }
                        ? TMetadata[K]['enumValues'][number][]
                        : string[]
                    : string[];
            }
            : never;

type UnionToIntersection<T> = (
    T extends unknown ? (arg: T) => void : never
) extends (arg: infer R) => void
    ? R
    : never;

export type DtoShapeByDefinition<
    TDefinition extends Record<string, FilterType>,
    TMetadata extends FilterMetadataMap<TDefinition> | undefined = undefined,
> = UnionToIntersection<
    {
        [K in keyof TDefinition]: DtoFieldShape<TDefinition, K, TMetadata>;
    }[keyof TDefinition]
>;

function toIso(value: DateTime | null): string | undefined {
    if (!value) return undefined;
    return value.toISO() ?? undefined;
}

function fromIso(value: unknown): DateTime | null {
    if (typeof value !== 'string') return null;
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : null;
}

function toFiniteNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseNumberOperator(value: unknown): NumberOperator | undefined {
    return value === 'eq' ||
    value === 'neq' ||
    value === 'gt' ||
    value === 'gte' ||
    value === 'lt' ||
    value === 'lte' ||
    value === 'range'
        ? value
        : undefined;
}

function toEnumSet(
    metadata: FilterMetadataMap<Record<string, FilterType>> | undefined,
    key: string,
): Set<string> | null {
    if (!metadata) return null;
    const fieldMetadata = metadata[key];
    if (fieldMetadata?.kind !== 'enum') return null;
    return new Set(fieldMetadata.enumValues);
}

export function filterValuesToDtoShape<
    TDefinition extends Record<string, FilterType>,
    TMetadata extends FilterMetadataMap<TDefinition> | undefined = undefined,
>(
    definition: TDefinition,
    values: NullableFilterValuesByDefinition<TDefinition>,
    metadata?: TMetadata,
): DtoShapeByDefinition<TDefinition, TMetadata> {
    const dto: Record<string, unknown> = {};
    const metadataRecord = metadata as
        | FilterMetadataMap<Record<string, FilterType>>
        | undefined;

    for (const key of Object.keys(definition) as (keyof TDefinition)[]) {
        const type = definition[key];
        const value = values[key];
        const enumSet = toEnumSet(metadataRecord, key as string);

        if (type === 'string') {
            if (value === null) {
                dto[key as string] = undefined;
                continue;
            }
            const asString = String(value);
            dto[key as string] =
                enumSet && !enumSet.has(asString) ? undefined : asString;
            continue;
        }

        if (type === 'string[]') {
            if (value === null) {
                dto[key as string] = undefined;
                continue;
            }
            const asArray = (value as string[]).filter((item) =>
                enumSet ? enumSet.has(item) : true,
            );
            dto[key as string] = asArray.length > 0 ? asArray : undefined;
            continue;
        }

        if (type === 'number') {
            const numberKey = key as string;
            const numberValue = value as NumberFilter | null;
            dto[`${numberKey}_op`] = numberValue?.op;
            dto[`${numberKey}_value`] = numberValue?.value;
            dto[`${numberKey}_min`] = numberValue?.min;
            dto[`${numberKey}_max`] = numberValue?.max;
            continue;
        }

        if (type === 'boolean') {
            dto[key as string] = value;
            continue;
        }

        const dateKey = key as string;
        const dateValue = value as DateFilter | null;
        dto[`${dateKey}_start`] = dateValue ? toIso(dateValue.start) : undefined;
        dto[`${dateKey}_end`] = dateValue ? toIso(dateValue.end) : undefined;
    }

    return dto as DtoShapeByDefinition<TDefinition, TMetadata>;
}

export function dtoShapeToFilterValues<
    TDefinition extends Record<string, FilterType>,
    TMetadata extends FilterMetadataMap<TDefinition> | undefined = undefined,
>(
    definition: TDefinition,
    dto: Partial<DtoShapeByDefinition<TDefinition, TMetadata>>,
    metadata?: TMetadata,
): NullableFilterValuesByDefinition<TDefinition> {
    const values: Record<string, unknown> = {};
    const dtoRecord = dto as Partial<Record<string, unknown>>;
    const metadataRecord = metadata as
        | FilterMetadataMap<Record<string, FilterType>>
        | undefined;

    for (const key of Object.keys(definition) as (keyof TDefinition)[]) {
        const type = definition[key];
        const enumSet = toEnumSet(metadataRecord, key as string);

        if (type === 'string') {
            const value = dtoRecord[key as string];
            if (typeof value !== 'string') {
                values[key as string] = null;
                continue;
            }
            values[key as string] =
                enumSet && !enumSet.has(value) ? null : value;
            continue;
        }

        if (type === 'string[]') {
            const value = dtoRecord[key as string];
            if (!Array.isArray(value)) {
                values[key as string] = null;
                continue;
            }
            const parsed = value.filter(
                (item): item is string => typeof item === 'string',
            );
            const filtered = parsed.filter((item) =>
                enumSet ? enumSet.has(item) : true,
            );
            values[key as string] = filtered.length > 0 ? filtered : null;
            continue;
        }

        if (type === 'number') {
            const numberKey = key as string;
            const op = parseNumberOperator(dtoRecord[`${numberKey}_op`]);
            const parsed: NumberFilter | null = op
                ? {
                    op,
                    value: toFiniteNumber(dtoRecord[`${numberKey}_value`]),
                    min: toFiniteNumber(dtoRecord[`${numberKey}_min`]),
                    max: toFiniteNumber(dtoRecord[`${numberKey}_max`]),
                }
                : null;
            values[key as string] = parsed;
            continue;
        }

        if (type === 'boolean') {
            const value = dtoRecord[key as string];
            values[key as string] = typeof value === 'boolean' ? value : null;
            continue;
        }

        const dateKey = key as string;
        const start = fromIso(dtoRecord[`${dateKey}_start`]);
        const end = fromIso(dtoRecord[`${dateKey}_end`]);
        values[key as string] =
            start || end
                ? ({
                    start,
                    end,
                } as DateFilter)
                : null;
    }

    return values as NullableFilterValuesByDefinition<TDefinition>;
}
